import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './TaskModal.module.scss';
import {
  IconX,
  IconPlus,
  IconSearch,
  IconLoader2
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';

import { Status, TaskDto, ProjectDto, CheckPointDto, TenantUserDto } from '../../api/model';
import { usePostTask } from '../../api/task-api/task-api';
import { usePostCheckpoint } from '../../api/check-point-api/check-point-api';
import { useGetDeliverObject } from '../../api/deliver-object-api/deliver-object-api';
import { useGetProjectIdMembers } from '../../api/project-api/project-api';
import { useNotifications } from '../../hooks/useNotifications';
import { useProjectPhases } from '../../hooks/useProjectPhases';
import {getPhaseLabel, getPhaseDisplayName} from '../../utils/phaseUtils';
import UserPicker from '../ui/UserPicker/UserPicker';
import RolePicker from '../ui/RolePicker/RolePicker';

interface CreateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTaskCreated?: () => void;
  deliverObjectId?: number;
  initialPhaseId?: number;
  project?: ProjectDto;
}

interface TaskFormData {
  // Task related fields
  name: string;
  description: string;
  assignedUserIds: number[];
  deliverObjectId?: number;
  // Checkpoint realted fields
  validationCriteria?: string;
  acceptanceCriteria?: string;
  validationMethod?: string;
  validationDate?: string;
  responsibleRole?: string;
}

const CreateTaskModal: React.FC<CreateTaskModalProps> = ({
  isOpen,
  onClose,
  onTaskCreated,
  deliverObjectId: initialDeliverObjectId,
  initialPhaseId,
  project
}) => {
  const { t } = useTranslation('tasks');
  const [formData, setFormData] = useState<TaskFormData>({
    name: '',
    description: '',
    assignedUserIds: [],
    deliverObjectId: initialDeliverObjectId
  });

  const [selectedPhaseId, setSelectedPhaseId] = useState<number | null>(initialPhaseId ?? null);
  const [phaseSearch, setPhaseSearch] = useState('');
  const [deliverableSearch, setDeliverableSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const formRef = React.useRef<HTMLFormElement>(null);

  // hooks
  const queryClient = useQueryClient();
  const createTaskMutation = usePostTask();
  const createCheckpointMutation = usePostCheckpoint();
  const { data: allDeliverObjects, isLoading: isLoadingDeliverObjects } = useGetDeliverObject();
  const { data: projectPhases, isLoading: isLoadingPhases } = useProjectPhases(project?.id);
  const { notifySuccess, notifyError } = useNotifications();
  const { data: projectMembers } = useGetProjectIdMembers(project?.id ?? 0, {
    query: { enabled: !!project?.id },
  });
  const memberUsers = useMemo<TenantUserDto[]>(
    () => (projectMembers ?? []).map(m => ({
      id: m.userId, username: m.username, firstName: m.firstName,
      lastName: m.lastName, tenantRoleDisplayName: m.tenantRoleDisplayName,
    })),
    [projectMembers]
  );

  // Lock preselect kinda approach for task creation
  const isPreselected = !!(initialPhaseId && initialDeliverObjectId);

  useEffect(() => {
    if (initialPhaseId && !selectedPhaseId) {
      setSelectedPhaseId(initialPhaseId);
    }
  }, [initialPhaseId]);

  // Resolve display names
  const preselectedPhaseName = useMemo(() => {
    if (!isPreselected || !projectPhases) return '';
    const phase = projectPhases.find(p => p.id === initialPhaseId);
    if (!phase) return `Phase #${initialPhaseId}`;
      const name = getPhaseDisplayName(phase);
      return phase.subTypeAgile ? `${name} - ${phase.subTypeAgile}` : name;
  }, [isPreselected, projectPhases, initialPhaseId]);

  const preselectedDeliverableName = useMemo(() => {
    if (!isPreselected || !allDeliverObjects) return '';
    const obj = allDeliverObjects.find((d: any) => d.id === initialDeliverObjectId);
    return obj?.name || `Deliverable #${initialDeliverObjectId}`;
  }, [isPreselected, allDeliverObjects, initialDeliverObjectId]);

  // Filter phases by search
  const filteredPhases = useMemo(() => {
    if (!projectPhases) return [];
    
    if (!phaseSearch.trim()) return projectPhases;
    
    const searchLower = phaseSearch.toLowerCase();
    return projectPhases.filter(phase =>
        getPhaseDisplayName(phase).toLowerCase().includes(searchLower) ||
      phase.subTypeAgile?.toLowerCase().includes(searchLower)
    );
  }, [projectPhases, phaseSearch]);

  // Filter deliver objects by selected phase
  const phaseDeliverObjects = useMemo(() => {
    if (!allDeliverObjects || !selectedPhaseId) return [];
    
    return allDeliverObjects.filter(obj => {
      const deliverableAny = obj as any;
      const phaseId = deliverableAny.phase?.id || deliverableAny.phase_id || deliverableAny.phaseId;
      return phaseId === selectedPhaseId;
    });
  }, [allDeliverObjects, selectedPhaseId]);

  // Filter deliverables (by search field)
  const filteredDeliverables = useMemo(() => {
    if (!deliverableSearch.trim()) return phaseDeliverObjects;
    
    const searchLower = deliverableSearch.toLowerCase();
    return phaseDeliverObjects.filter((obj: any) => 
      obj.name?.toLowerCase().includes(searchLower) ||
      obj.criticality?.toLowerCase().includes(searchLower)
    );
  }, [phaseDeliverObjects, deliverableSearch]);

  const handleInputChange = (field: keyof TaskFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = (): string | null => {
    if (!formData.name.trim()) {
      return t('create.validation.nameRequired');
    }
    if (formData.name.length > 50) {
      return t('create.validation.nameMaxLength');
    }
    if (formData.description.length > 250) {
      return t('create.validation.descMaxLength');
    }
    if (!selectedPhaseId) {
      return t('create.validation.phaseRequired');
    }
    if (!formData.deliverObjectId) {
      return t('create.validation.deliverableRequired');
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      notifyError(t('create.title'), validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      const taskData: TaskDto = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        assignedUserIds: formData.assignedUserIds,
        status: Status.PLANNED,
        progress: 0,
        deliverObjectId: formData.deliverObjectId
      };

      const createdTask = await createTaskMutation.mutateAsync({
        data: taskData
      });

      if (createdTask?.id) {
        const checkpointData: CheckPointDto = {
          taskId: createdTask.id,
          status: Status.PLANNED,
          validationCriteria: formData.validationCriteria?.trim() || undefined,
          acceptanceCriteria: formData.acceptanceCriteria?.trim() || undefined,
          validationMethod: formData.validationMethod?.trim() || undefined,
          validationDate: formData.validationDate || undefined,
          responsibleRole: formData.responsibleRole?.trim() || undefined
        };

        await createCheckpointMutation.mutateAsync({
          data: checkpointData
        });
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['/task'] }),
        queryClient.invalidateQueries({ predicate: (query) => typeof query.queryKey[0] === 'string' && (query.queryKey[0] as string).startsWith('/intelligence') }),
        queryClient.invalidateQueries({ queryKey: ['/project'] }),
        queryClient.invalidateQueries({ queryKey: ['projects'] }),
        queryClient.invalidateQueries({ predicate: (query) => typeof query.queryKey[0] === 'string' && (query.queryKey[0] as string).includes('/deliverobject') })
      ]);

      notifySuccess(t('create.title'), t('create.validation.success', { name: formData.name }));
      onTaskCreated?.();
      onClose();
      resetForm();
    } catch (error) {
      console.error('Error creating task or checkpoint:', error);
      notifyError(t('create.validation.creationFailed'), error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      assignedUserIds: [],
      deliverObjectId: initialDeliverObjectId,
    });
    setSelectedPhaseId(initialPhaseId ?? null);
    setPhaseSearch('');
    setDeliverableSearch('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.modalOverlay} onClick={handleClose}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <div className={styles.headerContent}>
              <h2 className={styles.modalTitle}>{t('create.title')}</h2>
              <p className={styles.headerDescription}>
                {t('create.description')}
              </p>
            </div>
            <button className={styles.closeButton} onClick={handleClose} type="button">
              <IconX size={20} />
            </button>
          </div>

            <div className={styles.modalBody}>
              <form ref={formRef} onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  {t('create.fields.name')} <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  className={styles.formInput}
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder={t('create.fields.namePlaceholder')}
                  required
                  autoFocus
                  disabled={isSubmitting}
                  maxLength={50}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>{t('create.fields.description')}</label>
                <textarea
                  className={styles.formTextarea}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder={t('create.fields.descriptionPlaceholder')}
                  rows={3}
                  disabled={isSubmitting}
                  maxLength={250}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>{t('create.fields.assignedPerson')}</label>
                <UserPicker
                  multi
                  value={formData.assignedUserIds}
                  onChange={(ids) => handleInputChange('assignedUserIds', ids)}
                  disabled={isSubmitting}
                  placeholder={t('create.fields.assignedPersonPlaceholder')}
                  users={project?.id ? memberUsers : undefined}
                />
              </div>

              {isPreselected ? (
                <>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>{t('create.fields.phase')}</label>
                    <div
                      className={styles.formInput}
                      style={{ backgroundColor: '#F3F4F6', color: '#374151', cursor: 'default' }}
                    >
                      {preselectedPhaseName || t('common:loading')}
                    </div>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>{t('create.fields.deliverable')}</label>
                    <div
                      className={styles.formInput}
                      style={{ backgroundColor: '#F3F4F6', color: '#374151', cursor: 'default' }}
                    >
                      {preselectedDeliverableName || t('common:loading')}
                    </div>
                  </div>
                </>
              ) : (
                <>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  {t('create.fields.phase')} <span className={styles.required}>*</span>
                </label>
                
                {isLoadingPhases ? (
                  <div className={styles.formInput} style={{ display: 'flex', alignItems: 'center', color: '#6B7280' }}>
                    {t('create.phases.loading')}
                  </div>
                ) : projectPhases && projectPhases.length > 0 ? (
                  <>
                    <div style={{ position: 'relative', marginBottom: '8px' }}>
                      <IconSearch 
                        size={18} 
                        style={{ 
                          position: 'absolute', 
                          left: '12px', 
                          top: '50%', 
                          transform: 'translateY(-50%)', 
                          color: '#6B7280',
                          pointerEvents: 'none'
                        }} 
                      />
                      <input
                        type="text"
                        className={styles.formInput}
                        value={phaseSearch}
                        onChange={(e) => setPhaseSearch(e.target.value)}
                        placeholder={t('create.phases.search')}
                        style={{ paddingLeft: '40px' }}
                        disabled={isSubmitting}
                      />
                    </div>
                    
                    <div className={styles.deliverableList}>
                      {!selectedPhaseId && (
                        <div className={`${styles.deliverableItem} ${styles.placeholder}`}>
                          {t('create.phases.selectFirst')}
                        </div>
                      )}
                      {filteredPhases.map((phase) => (
                        <div
                          key={phase.id}
                          className={`${styles.deliverableItem} ${selectedPhaseId === phase.id ? styles.selected : ''}`}
                          onClick={() => {
                            if (!isSubmitting) {
                              setSelectedPhaseId(phase.id || null);
                              setFormData(prev => ({ ...prev, deliverObjectId: undefined }));
                              setDeliverableSearch('');
                            }
                          }}
                          style={{ cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                        >
                          <div className={styles.deliverableName}>
                              {getPhaseLabel(phase, filteredPhases)}
                            {phase.subTypeAgile && (
                              <span style={{ color: '#6B7280', marginLeft: '8px' }}>
                                ({phase.subTypeAgile})
                              </span>
                            )}
                          </div>
                          {(phase.status || phase.endTime) && (
                            <div className={styles.deliverableMeta}>
                              {phase.status && (
                                <span className={styles.deliverableMetaItem}>
                                  {phase.status}
                                </span>
                              )}
                              {phase.endTime && (
                                <span className={styles.deliverableMetaItem}>
                                  End: {phase.endTime}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {filteredPhases.length === 0 && phaseSearch && (
                      <p className={styles.inputHelper}>
                        {t('create.phases.search')}
                      </p>
                    )}
                  </>
                ) : (
                  <div className={styles.inputHelper} style={{ padding: '12px', backgroundColor: '#FEF3C7', borderRadius: '6px', color: '#92400E' }}>
                    {t('create.phases.none')}
                  </div>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  {t('create.fields.deliverable')} <span className={styles.required}>*</span>
                </label>
                
                {!selectedPhaseId ? (
                  <div className={styles.inputHelper} style={{ padding: '12px', backgroundColor: '#F3F4F6', borderRadius: '6px', color: '#6B7280' }}>
                    {t('create.deliverables.selectFirst')}
                  </div>
                ) : isLoadingDeliverObjects ? (
                  <div className={styles.formInput} style={{ display: 'flex', alignItems: 'center', color: '#6B7280' }}>
                    {t('create.deliverables.loading')}
                  </div>
                ) : phaseDeliverObjects.length > 0 ? (
                  <>
                    <div style={{ position: 'relative', marginBottom: '8px' }}>
                      <IconSearch 
                          size={18} 
                          style={{ 
                            position: 'absolute', 
                            left: '12px', 
                            top: '50%', 
                            transform: 'translateY(-50%)', 
                            color: '#6B7280',
                            pointerEvents: 'none'
                          }} 
                        />
                        <input
                          type="text"
                          className={styles.formInput}
                          value={deliverableSearch}
                          onChange={(e) => setDeliverableSearch(e.target.value)}
                          placeholder={t('create.deliverables.select')}
                          style={{ paddingLeft: '40px' }}
                          disabled={isSubmitting}
                        />
                      </div>
                      
                      <div className={styles.deliverableList}>
                        {!formData.deliverObjectId && (
                          <div className={`${styles.deliverableItem} ${styles.placeholder}`}>
                            {t('create.deliverables.select')}
                          </div>
                        )}
                        {filteredDeliverables.map((obj) => (
                          <div
                            key={obj.id}
                            className={`${styles.deliverableItem} ${formData.deliverObjectId === obj.id ? styles.selected : ''}`}
                            onClick={() => !isSubmitting && handleInputChange('deliverObjectId', obj.id)}
                            style={{ cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                          >
                              <div
                                  className={styles.deliverableName}>{(obj.name || t('create.deliverables.unnamedItem')).replaceAll('_', ' ')}</div>
                            {(obj.criticality || obj.endDate) && (
                              <div className={styles.deliverableMeta}>
                                {obj.criticality && (
                                  <span className={styles.deliverableMetaItem}>
                                    {obj.criticality}
                                  </span>
                                )}
                                {obj.endDate && (
                                  <span className={styles.deliverableMetaItem}>
                                    Due: {obj.endDate}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {filteredDeliverables.length === 0 && deliverableSearch && (
                        <p className={styles.inputHelper}>
                          {t('create.deliverables.noMatch')}
                        </p>
                      )}
                    </>
                  ) : (
                    <div className={styles.inputHelper} style={{ padding: '12px', backgroundColor: '#FEF3C7', borderRadius: '6px', color: '#92400E' }}>
                      {t('create.deliverables.none')}
                    </div>
                  )}
                </div>
                </>
              )}
              <div className={styles.checkpointSection}>
                <h3 className={styles.sectionTitle}>{t('create.fields.checkpointDetails')}</h3>
                <p className={styles.sectionDescription}>
                  {t('create.sectionDescription')}
                </p>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  {t('create.fields.validationCriteria')}
                </label>
                <textarea
                  className={styles.formTextarea}
                  value={formData.validationCriteria || ''}
                  onChange={(e) => handleInputChange('validationCriteria', e.target.value)}
                  placeholder={t('create.fields.validationCriteriaPlaceholder')}
                  rows={2}
                  disabled={isSubmitting}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  {t('create.fields.acceptanceCriteria')}
                </label>
                <textarea
                  className={styles.formTextarea}
                  value={formData.acceptanceCriteria || ''}
                  onChange={(e) => handleInputChange('acceptanceCriteria', e.target.value)}
                  placeholder={t('create.fields.acceptanceCriteriaPlaceholder')}
                  rows={2}
                  disabled={isSubmitting}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>{t('create.fields.validationMethod')}</label>
                <input
                  type="text"
                  className={styles.formInput}
                  value={formData.validationMethod || ''}
                  onChange={(e) => handleInputChange('validationMethod', e.target.value)}
                  placeholder={t('create.fields.validationMethodPlaceholder')}
                  disabled={isSubmitting}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  {t('create.fields.validationDate')}
                </label>
                <input
                  type="date"
                  className={styles.formInput}
                  value={formData.validationDate || ''}
                  onChange={(e) => handleInputChange('validationDate', e.target.value)}
                  disabled={isSubmitting}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>{t('create.fields.responsibleRole')}</label>
                <RolePicker
                  className={styles.formSelect}
                  value={formData.responsibleRole}
                  onChange={(roleKey) => handleInputChange('responsibleRole', roleKey)}
                  disabled={isSubmitting}
                  placeholder={t('create.fields.responsibleRolePlaceholder')}
                />
              </div>
              </form>
            </div>

            <div className={styles.modalFooter}>
              <div className={styles.footerActions}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  {t('create.buttons.cancel')}
                </button>
                <button
                  type="button"
                  className={styles.submitButton}
                  disabled={isSubmitting || !selectedPhaseId || (!isPreselected && phaseDeliverObjects.length === 0)}
                  onClick={() => formRef.current?.requestSubmit()}
                >
                  {isSubmitting ? (
                    <>
                      <IconLoader2 size={18} className={styles.spinning} />
                      {t('create.buttons.creating')}
                    </>
                  ) : (
                    <>
                      <IconPlus size={18} />
                      {t('create.buttons.create')}
                    </>
                  )}
                </button>
              </div>
            </div>
        </div>
      </div>
    </>
  );
};

export default CreateTaskModal;
